import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Pause, Play, Download, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import videoApi from "@/lib/api";
import { getApiErrorMessage } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import VoxaraScribe from "@/components/shared/VoxaraScribe";
import VoxaraWaiting from "@/components/shared/VoxaraWaiting";
import ShareAudioGuide from "@/components/shared/ShareAudioGuide";
import { detectSystemAudioSupport } from "@/lib/systemAudioSupport";
import { startMp3Capture } from "@/lib/mp3Recorder";
import {
  savePendingRecording, loadPendingRecording, clearPendingRecording, markPendingUploaded,
  beginLiveRecording, appendLiveChunk, clearLiveRecording, recoverInterruptedRecording,
} from "@/lib/pendingRecording";
import { useAuth } from "@/context/AuthContext";

const RECORDING_TYPES = [
  { id: "audiencia_judicial", es: "Audiencia o juicio", en: "Court hearing or trial" },
  { id: "declaracion_testimonio", es: "Declaración o testimonio", en: "Statement or testimony" },
  { id: "reunion_asamblea", es: "Reunión o asamblea", en: "Meeting or assembly" },
  { id: "entrevista", es: "Entrevista", en: "Interview" },
  { id: "clase_conferencia", es: "Clase o conferencia", en: "Lecture or class" },
  { id: "otro", es: "Otro / no estoy seguro", en: "Other / not sure" },
];

const LANGUAGES = [
  { id: "es", es: "Español", en: "Spanish" },
  { id: "en", es: "Inglés", en: "English" },
  { id: "auto", es: "Detectar automáticamente", en: "Detect automatically" },
];

const WARN_AFTER_SECONDS = 3 * 60 * 60;   // nudge at 3h
const STOP_AFTER_SECONDS = 4 * 60 * 60;   // hard stop at 4h so a forgotten tab cannot run all night
const SILENCE_GRACE_MS = 8000;
const COPY_RETENTION_SECONDS = 30 * 60;   // the browser copy self-destructs after half an hour

// Opus in webm first (smallest, and what Whisper handles best), then Safari's only option.
const RECORDER_MIMES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];
const pickRecorderMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  return RECORDER_MIMES.find((m) => MediaRecorder.isTypeSupported(m)) || "";
};
const extensionFor = (mime) => {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
};

const two = (n) => String(n).padStart(2, "0");
const fmtClock = (s) => `${two(Math.floor(s / 3600))}:${two(Math.floor((s % 3600) / 60))}:${two(Math.floor(s % 60))}`;
const fmtCountdown = (s) => `${two(Math.floor(s / 60))}:${two(Math.floor(s % 60))}`;
const fmtSize = (b) => (b >= 1024 * 1024 * 1024 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(1)} MB`);

export default function LiveRecordingPanel({ onRecordingUploaded }) {
  const { language } = useLanguage();
  const { holdSession } = useAuth();
  const t = (es, en) => (language === "es" ? es : en);

  const [phase, setPhase] = useState("setup");        // setup | armed | recording | paused | uploading | done
  const [transcriptionLanguage, setTranscriptionLanguage] = useState("");
  // Neutral by default, matching the regular upload: presetting a court hearing would send legal
  // vocabulary hints to Whisper for someone recording a lecture.
  const [recordingType, setRecordingType] = useState("otro");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [silent, setSilent] = useState(false);
  const [warned, setWarned] = useState(false);
  const [result, setResult] = useState(null);         // { url, name, size }
  const [uploadPct, setUploadPct] = useState(0);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("default");
  const [captureMeeting, setCaptureMeeting] = useState(true);
  const [captureMic, setCaptureMic] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [audioSupport, setAudioSupport] = useState(null);
  // Touch plus no screen-capture API is a phone or tablet; the API is missing on every mobile
  // browser, and on iOS the engine is WebKit no matter which browser is installed.
  const onPhone = typeof navigator !== "undefined"
    && (navigator.maxTouchPoints || 0) > 0
    && !(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function");
  const [copySecondsLeft, setCopySecondsLeft] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);   // set when a reload interrupted the send
  const [recovered, setRecovered] = useState(null);           // a recording an interruption cut short
  const [probeLevel, setProbeLevel] = useState(0);
  const [probing, setProbing] = useState(false);

  const levelRef = useRef(0);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const mixedStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const tickRef = useRef(null);
  const lastSoundRef = useRef(Date.now());
  const startedAtRef = useRef(0);
  const mp3Ref = useRef(null);
  const resultRef = useRef(null);
  const probeStreamRef = useRef(null);
  const probeCtxRef = useRef(null);
  const probeRafRef = useRef(null);

  const browserSupported = typeof navigator !== "undefined"
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === "function";

  const releaseEverything = useCallback(() => {
    // stopRecording() clears this before it gets here; anything still set means the recording was
    // abandoned rather than finished, and the encoder worker would otherwise outlive the page.
    if (mp3Ref.current) {
      mp3Ref.current.abort();
      mp3Ref.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    [displayStreamRef, micStreamRef, mixedStreamRef].forEach((ref) => {
      if (ref.current) {
        ref.current.getTracks().forEach((tr) => tr.stop());
        ref.current = null;
      }
    });
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  useEffect(() => () => releaseEverything(), [releaseEverything]);

  // Warn before leaving mid-recording: the audio only exists in this tab.
  useEffect(() => {
    if (phase !== "recording" && phase !== "paused" && phase !== "uploading") return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  useEffect(() => {
    let cancelled = false;
    detectSystemAudioSupport()
      .then((support) => { if (!cancelled) setAudioSupport(support); })
      .catch(() => { if (!cancelled) setAudioSupport({ os: "other", systemAudio: true }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { resultRef.current = result; }, [result]);

  // The idle timer logs people out after ten minutes of not touching this tab, and it asks the
  // server whether to stand down. A recording in progress is invisible to the server, so it has to
  // say so itself — otherwise a lawyer who starts recording and switches to Zoom is logged out
  // mid-hearing, which unmounts this panel and destroys the recording outright.
  useEffect(() => {
    const recordingIsLive = phase === "recording" || phase === "paused" || phase === "uploading";
    if (!recordingIsLive || typeof holdSession !== "function") return undefined;
    return holdSession(`live-recording:${phase}`);
  }, [phase, holdSession]);

  // A reload lands here: whatever survived in IndexedDB is put back on screen, with the time that
  // actually remains rather than a fresh countdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await loadPendingRecording();
      if (cancelled) return;

      // Nothing finished, but chunks on disk mean a recording was cut off rather than stopped.
      if (!stored) {
        const salvaged = await recoverInterruptedRecording();
        if (!cancelled && salvaged) {
          const stamp = new Date(salvaged.startedAt || Date.now())
            .toISOString().slice(0, 16).replace("T", "_").replace(":", "-");
          setRecovered({
            ...salvaged,
            url: URL.createObjectURL(salvaged.blob),
            name: `voxara-recuperado-${stamp}.${extensionFor(salvaged.mimeType)}`,
          });
        }
        return;
      }
      if (stored.expired) {
        setResult({ url: null, name: stored.name, size: stored.size, expired: true });
        setPhase("done");
        return;
      }
      setResult({ url: URL.createObjectURL(stored.blob), name: stored.name, size: stored.size });
      setCopySecondsLeft(stored.secondsLeft);
      setPhase("done");
      if (!stored.uploaded) {
        setPendingUpload({ blob: stored.uploadBlob || stored.blob, name: stored.uploadName || stored.name,
                           language: stored.language, recordingType: stored.recordingType });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const listDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const all = await navigator.mediaDevices.enumerateDevices();
    setDevices(all.filter((d) => d.kind === "audioinput"));
  }, []);

  useEffect(() => {
    listDevices();
    if (!navigator.mediaDevices?.addEventListener) return undefined;
    navigator.mediaDevices.addEventListener("devicechange", listDevices);
    return () => navigator.mediaDevices.removeEventListener("devicechange", listDevices);
  }, [listDevices]);

  const stopProbe = useCallback(() => {
    if (probeRafRef.current) cancelAnimationFrame(probeRafRef.current);
    probeRafRef.current = null;
    if (probeStreamRef.current) {
      probeStreamRef.current.getTracks().forEach((tr) => tr.stop());
      probeStreamRef.current = null;
    }
    if (probeCtxRef.current && probeCtxRef.current.state !== "closed") {
      probeCtxRef.current.close().catch(() => {});
    }
    probeCtxRef.current = null;
    setProbing(false);
    setProbeLevel(0);
  }, []);

  // Opens the chosen input briefly so the user can see it is actually picking up sound.
  const probeDevice = useCallback(async () => {
    setError("");
    stopProbe();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId && deviceId !== "default" ? { deviceId: { exact: deviceId } } : true,
      });
      probeStreamRef.current = stream;
      await listDevices();   // labels only appear once permission is granted

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      probeCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      setProbing(true);

      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = Math.abs(buf[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        setProbeLevel(Math.min(1, peak * 3.2));
        probeRafRef.current = requestAnimationFrame(tick);
      };
      probeRafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      stopProbe();
      setError(t("No pudimos abrir esa entrada de audio. Elige otra o revisa los permisos del micrófono.",
                 "We could not open that audio input. Pick another one or check the microphone permissions."));
    }
  }, [deviceId, listDevices, stopProbe, t]);

  useEffect(() => () => stopProbe(), [stopProbe]);

  // Counts the browser copy down and frees it at zero. Revoking the object URL is what actually
  // drops the recording from memory; leaving it alive was a real leak as well as a privacy problem.
  useEffect(() => {
    if (copySecondsLeft === null) return undefined;
    if (copySecondsLeft <= 0) {
      setResult((previous) => {
        if (previous?.url) URL.revokeObjectURL(previous.url);
        return previous ? { ...previous, url: null, expired: true } : previous;
      });
      setPendingUpload(null);
      clearPendingRecording();
      return undefined;
    }
    const timer = setTimeout(() => setCopySecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [copySecondsLeft]);

  // A tab closed mid-window should not leave the blob behind either.
  useEffect(() => () => {
    if (resultRef.current?.url) URL.revokeObjectURL(resultRef.current.url);
  }, []);

  const readLevel = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(buf);
    let peak = 0;
    for (let i = 0; i < buf.length; i += 1) {
      const v = Math.abs(buf[i] - 128) / 128;
      if (v > peak) peak = v;
    }
    const level = Math.min(1, peak * 3.2);
    levelRef.current = level;
    if (level > 0.045) lastSoundRef.current = Date.now();
    setSilent(Date.now() - lastSoundRef.current > SILENCE_GRACE_MS);
    rafRef.current = requestAnimationFrame(readLevel);
  }, []);

  const arm = useCallback(async () => {
    setError("");
    if (!transcriptionLanguage) {
      toast.error(t("Selecciona el idioma del audio antes de continuar.", "Select the audio language before continuing."));
      return;
    }
    if (captureMeeting && !browserSupported) {
      setError(t(
        "Tu navegador no puede capturar el audio de una videollamada. Abre Voxara en Chrome o Edge.",
        "Your browser cannot capture meeting audio. Open Voxara in Chrome or Edge.",
      ));
      return;
    }

    stopProbe();
    try {
      let display = null;
      if (captureMeeting) {
        const wholeComputerAudioPossible = audioSupport ? audioSupport.systemAudio : true;

        display = await navigator.mediaDevices.getDisplayMedia({
          // Open the picker on the surface that can actually carry audio here: the whole screen
          // where system audio exists, a browser tab where it does not. selfBrowserSurface hides
          // Voxara's own tab, which is never what the user wants to record.
          video: { displaySurface: wholeComputerAudioPossible ? "monitor" : "browser" },
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          systemAudio: "include",
          selfBrowserSurface: "exclude",
        });
        displayStreamRef.current = display;

        const abandonShare = (message) => {
          display.getTracks().forEach((tr) => tr.stop());
          displayStreamRef.current = null;
          setError(message);
        };

        // The constraint above is only a hint — Chrome still offers tabs and windows — and a tab
        // records a fraction of what the checkbox promised. But where the computer cannot deliver
        // system audio at all, a tab is the only thing that works, so it is accepted with a warning
        // rather than refused.
        const surface = display.getVideoTracks()[0]?.getSettings?.().displaySurface;

        if (surface && surface !== "monitor" && wholeComputerAudioPossible) {
          abandonShare(surface === "browser"
            ? t("Compartiste una pestaña del navegador, así que solo quedaría grabado el sonido de esa pestaña. Vuelve a intentarlo y elige «Toda la pantalla»: la pantalla donde esté sonando el audio que quieres grabar.",
                "You shared a browser tab, so only that tab's sound would be recorded. Try again and pick \u201cEntire screen\u201d \u2014 the screen where the audio is playing.")
            : t("Compartiste una sola ventana. Vuelve a intentarlo y elige «Toda la pantalla»: la pantalla donde esté sonando el audio que quieres grabar.",
                "You shared a single window. Try again and pick \u201cEntire screen\u201d \u2014 the screen where the audio is playing."));
          return;
        }

        if (display.getAudioTracks().length === 0) {
          abandonShare(wholeComputerAudioPossible
            ? t("No se compartió el audio. Vuelve a intentarlo: elige «Toda la pantalla» y marca la casilla de compartir el audio del sistema.",
                "Audio was not shared. Try again: pick \u201cEntire screen\u201d and tick the share-system-audio box.")
            : t("No se compartió el audio. En este computador hay que compartir la pestaña donde está el audio, con la casilla de audio marcada.",
                "Audio was not shared. On this computer you must share the tab the audio comes from, with the audio box ticked."));
          return;
        }

        if (surface === "browser") {
          toast.message(t("Grabaremos solo el sonido de esa pestaña.",
                          "We will record that tab's sound only."));
        }
      }

      let mic = null;
      if (captureMic) {
        try {
          mic = await navigator.mediaDevices.getUserMedia({
            audio: deviceId && deviceId !== "default" ? { deviceId: { exact: deviceId } } : true,
          });
          micStreamRef.current = mic;
        } catch (micErr) {
          console.warn("Selected audio input unavailable", micErr);
          if (!display) {
            setError(t("No pudimos abrir la entrada de audio seleccionada.",
                       "We could not open the selected audio input."));
            return;
          }
          toast.message(t("Grabaremos solo el audio del computador (sin micrófono).",
                          "Recording the computer's audio only (no microphone)."));
        }
      }

      if (!display && !mic) {
        setError(t("Elige al menos una fuente de audio para grabar.",
                   "Pick at least one audio source to record."));
        return;
      }

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();
      if (display) ctx.createMediaStreamSource(display).connect(dest);
      if (mic) ctx.createMediaStreamSource(mic).connect(dest);

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(dest.stream).connect(analyser);
      analyserRef.current = analyser;

      // The portable copy is encoded from the same mix the recorder gets, in parallel with it.
      try {
        mp3Ref.current = await startMp3Capture(ctx, ctx.createMediaStreamSource(dest.stream));
      } catch (mp3Err) {
        console.warn("MP3 copy unavailable, the webm download still works", mp3Err);
        mp3Ref.current = null;
      }

      mixedStreamRef.current = dest.stream;
      lastSoundRef.current = Date.now();
      rafRef.current = requestAnimationFrame(readLevel);

      // If the user stops sharing from Chrome's own bar, end the recording cleanly.
      (display ? display.getVideoTracks() : []).forEach((tr) => {
        tr.addEventListener("ended", () => {
          if (recorderRef.current && recorderRef.current.state !== "inactive") stopRecording();
        });
      });

      setPhase("armed");
    } catch (err) {
      if (err && err.name === "NotAllowedError") {
        setError(t("Cancelaste el permiso para compartir. Vuelve a intentarlo cuando estés listo.",
                   "You dismissed the sharing prompt. Try again when you are ready."));
      } else {
        setError(getApiErrorMessage(err, t("No pudimos acceder al audio.", "We could not access the audio."), language));
      }
    }
  }, [audioSupport, browserSupported, captureMeeting, captureMic, deviceId, language, readLevel, stopProbe, t, transcriptionLanguage]);

  const beginRecording = useCallback(() => {
    const stream = mixedStreamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const mime = pickRecorderMime();
    const rec = new MediaRecorder(stream, mime
      ? { mimeType: mime, audioBitsPerSecond: 96000 }
      : { audioBitsPerSecond: 96000 });
    rec.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      chunksRef.current.push(e.data);
      // Also written to disk, so an interruption costs the last few seconds instead of the whole
      // recording. Not awaited: a slow write must never hold up the recorder.
      appendLiveChunk(e.data);
    };
    beginLiveRecording({
      mimeType: mime || "audio/webm",
      timesliceMs: 4000,
      language: transcriptionLanguage,
      recordingType,
    });
    rec.start(4000); // flush regularly so a crash loses seconds, not hours
    recorderRef.current = rec;
    startedAtRef.current = Date.now();
    setElapsed(0);
    setWarned(false);
    setPhase("recording");
    tickRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(secs);
      if (secs >= STOP_AFTER_SECONDS) {
        toast.message(t("Se alcanzó el límite de 4 horas. Detuvimos la grabación.",
                        "The 4 hour limit was reached. Recording stopped."));
        stopRecording();
      } else if (secs >= WARN_AFTER_SECONDS && !warned) {
        setWarned(true);
        toast.message(t("Llevas 3 horas grabando. Se detendrá automáticamente a las 4.",
                        "You have been recording for 3 hours. It will stop automatically at 4."));
      }
    }, 1000);
  }, [recordingType, t, transcriptionLanguage, warned]);

  // Shared by a fresh recording and by a reload that interrupted the first attempt.
  const sendToPipeline = useCallback(async (blob, name, lang, kind) => {
    setPhase("uploading");
    setError("");
    const file = new File([blob], name, { type: blob.type || "audio/webm" });
    let uploaded = null;
    try {
      uploaded = await videoApi.upload(
        file, lang, (pct) => setUploadPct(pct), () => {},
        { recordingType: kind, isLiveRecording: true },
      );
    } catch (err) {
      setPhase("done");
      setPendingUpload({ blob, name, language: lang, recordingType: kind });
      setError(getApiErrorMessage(err, t(
        "La grabación no se pudo subir, pero tu copia sigue disponible aquí.",
        "The recording could not be uploaded, but your copy is still available here.",
      ), language));
      return false;
    }

    // The upload is done at this point. Refreshing the dashboard is a nicety, so a failure
    // here must never be reported as a failed recording.
    setPhase("done");
    setPendingUpload(null);
    markPendingUploaded();
    toast.success(t("Grabación subida. La estamos procesando.", "Recording uploaded. We are processing it."));
    try {
      if (onRecordingUploaded) onRecordingUploaded(uploaded, file);
    } catch (refreshErr) {
      console.warn("Recording uploaded, but the dashboard list could not refresh", refreshErr);
    }
    return true;
  }, [language, onRecordingUploaded, t]);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;

    rec.onstop = async () => {
      const recordedMime = (rec.mimeType || "audio/webm").split(";")[0];
      const blob = new Blob(chunksRef.current, { type: recordedMime });
      chunksRef.current = [];
      const stamp = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "-");
      const name = `voxara-${stamp}.${extensionFor(recordedMime)}`;

      // 1. the client's copy — MP3 when the encoder produced one, since webm opens almost nowhere
      //    outside a browser. The webm is the fallback, never a failure.
      let copyBlob = blob;
      let copyName = name;
      const encoder = mp3Ref.current;
      mp3Ref.current = null;
      if (encoder) {
        try {
          const mp3 = await encoder.stop();
          if (mp3) {
            copyBlob = mp3;
            copyName = `voxara-${stamp}.mp3`;
          }
        } catch (mp3Err) {
          console.warn("MP3 copy failed, offering the original recording", mp3Err);
        }
      }

      const url = URL.createObjectURL(copyBlob);
      setResult({ url, name: copyName, size: copyBlob.size });
      setCopySecondsLeft(COPY_RETENTION_SECONDS);
      releaseEverything();

      // Stored before the upload starts, not after: a reload during the upload is exactly the case
      // that used to lose the recording outright. The webm rides along so an interrupted send can
      // be retried, and is dropped from storage once the pipeline confirms it.
      await savePendingRecording({
        blob: copyBlob,
        name: copyName,
        expiresAt: Date.now() + COPY_RETENTION_SECONDS * 1000,
        uploadBlob: blob,
        uploadName: name,
        uploadMime: blob.type,
        language: transcriptionLanguage,
        recordingType,
      });

      // The finished file is stored now, so the chunk log has nothing left to protect.
      await clearLiveRecording();

      // 2. then the usual pipeline, which receives the original recording untouched
      await sendToPipeline(blob, name, transcriptionLanguage, recordingType);
    };
    rec.stop();
    recorderRef.current = null;
  }, [recordingType, releaseEverything, sendToPipeline, transcriptionLanguage]);

  const togglePause = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setPhase("paused");
    } else if (rec.state === "paused") {
      rec.resume();
      const base = Date.now() - elapsed * 1000;
      startedAtRef.current = base;
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - base) / 1000)), 1000);
      setPhase("recording");
    }
  }, [elapsed]);

  const reset = useCallback(() => {
    releaseEverything();
    setPhase("setup");
    setElapsed(0);
    setError("");
    setResult((previous) => {
      if (previous?.url) URL.revokeObjectURL(previous.url);
      return null;
    });
    setCopySecondsLeft(null);
    setPendingUpload(null);
    clearPendingRecording();
    setUploadPct(0);
    setSilent(false);
  }, [releaseEverything]);

  const isLive = phase === "recording";
  const statusText = () => {
    if (phase === "paused") return t("En pausa — no se está capturando audio", "Paused — no audio is being captured");
    if (phase === "armed") return t("Listo para grabar", "Ready to record");
    if (silent && isLive) return t("No está entrando audio — revisa la videollamada", "No audio is arriving — check the meeting");
    if (isLive) return t("Escuchando y transcribiendo", "Listening and transcribing");
    return t("Sin grabación activa", "No active recording");
  };

  return (
    <div className="space-y-4" data-testid="live-recording-panel">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold">{t("Grabación en vivo", "Live recording")}</h3>
          <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                data-testid="live-recording-admin-badge">
            {t("Solo admin · en pruebas", "Admin only · testing")}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {t("Para reuniones, audiencias, entrevistas o clases por Zoom, Meet o Teams. Capturamos el audio de la videollamada junto con tu micrófono.",
             "For meetings, hearings, interviews or lectures on Zoom, Meet or Teams. We capture the call audio together with your microphone.")}
        </p>

        {!browserSupported && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-yellow-500/60 bg-yellow-500/15 p-4"
               data-testid="live-unsupported-notice">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-yellow-400" />
            <div>
              <p className="text-sm font-bold text-yellow-200 leading-6">
                {onPhone
                  ? t("Para grabar, usa un computador.", "To record, use a computer.")
                  : t("Este navegador no puede capturar el audio del computador.", "This browser cannot capture the computer's audio.")}
              </p>
              <p className="mt-1 text-xs text-yellow-200/80 leading-5">
                {onPhone
                  ? t("Los celulares y las tabletas no pueden capturar el audio de la videollamada. Abre Voxara en un computador con Chrome o Edge.",
                      "Phones and tablets cannot capture the meeting audio. Open Voxara on a computer with Chrome or Edge.")
                  : t("Abre Voxara en Chrome o Edge sobre Windows, o en un Mac con macOS 14.2 o superior.",
                      "Open Voxara in Chrome or Edge on Windows, or on a Mac running macOS 14.2 or later.")}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="live-recording-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {recovered && (
          <div className="mt-4 rounded-xl border-2 border-yellow-500/60 bg-yellow-500/15 p-4" data-testid="live-recovered-notice">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-yellow-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-yellow-200 leading-6">
                  {t("Encontramos una grabación que quedó a medias.",
                     "We found a recording that was cut short.")}
                </p>
                <p className="mt-1 text-xs text-yellow-200/80 leading-5">
                  {t(`Se interrumpió antes de que la detuvieras, pero alcanzamos a guardar ${fmtClock(recovered.approximateSeconds)} de audio (${fmtSize(recovered.blob.size)}). Puedes descargarla, enviarla a transcribir, o descartarla.`,
                     `It stopped before you did, but we saved ${fmtClock(recovered.approximateSeconds)} of audio (${fmtSize(recovered.blob.size)}). You can download it, send it for transcription, or discard it.`)}
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm" className="btn-glow">
                    <a href={recovered.url} download={recovered.name} data-testid="live-recovered-download">
                      <Download className="w-4 h-4 mr-2" />{t("Descargar", "Download")}
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="live-recovered-send"
                    onClick={async () => {
                      const salvaged = recovered;
                      setRecovered(null);
                      // Same landing as a finished recording: the copy stays downloadable on its
                      // own clock while the upload runs, rather than vanishing into an empty panel.
                      setResult({ url: salvaged.url, name: salvaged.name, size: salvaged.blob.size });
                      setCopySecondsLeft(COPY_RETENTION_SECONDS);
                      await savePendingRecording({
                        blob: salvaged.blob,
                        name: salvaged.name,
                        expiresAt: Date.now() + COPY_RETENTION_SECONDS * 1000,
                        uploadBlob: salvaged.blob,
                        uploadName: salvaged.name,
                        uploadMime: salvaged.blob.type,
                        language: salvaged.language || "auto",
                        recordingType: salvaged.recordingType || "otro",
                      });
                      await clearLiveRecording();
                      await sendToPipeline(salvaged.blob, salvaged.name,
                                           salvaged.language || "auto", salvaged.recordingType || "otro");
                    }}
                  >
                    {t("Enviar a transcribir", "Send for transcription")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="live-recovered-discard"
                    onClick={() => {
                      if (recovered.url) URL.revokeObjectURL(recovered.url);
                      setRecovered(null);
                      clearLiveRecording();
                      toast.message(t("Grabación descartada.", "Recording discarded."));
                    }}
                  >
                    {t("Descartar", "Discard")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {phase === "setup" && browserSupported && (
          <>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="live-language">
                  {t("Idioma del audio", "Audio language")} <span className="text-destructive">*</span>
                </label>
                <Select value={transcriptionLanguage} onValueChange={setTranscriptionLanguage}>
                  <SelectTrigger id="live-language" data-testid="live-language-select">
                    <SelectValue placeholder={t("Selecciona un idioma", "Select a language")} />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{language === "es" ? l.es : l.en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="live-kind">
                  {t("Tipo de grabación", "Recording type")}
                </label>
                <Select value={recordingType} onValueChange={setRecordingType}>
                  <SelectTrigger id="live-kind" data-testid="live-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECORDING_TYPES.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{language === "es" ? r.es : r.en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4">
              <p className="text-sm font-medium mb-3">{t("¿Qué audio quieres capturar?", "Which audio should we capture?")}</p>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={captureMeeting}
                  onChange={(e) => setCaptureMeeting(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[color:hsl(var(--primary))]"
                  data-testid="live-capture-meeting-toggle"
                />
                <span className="text-sm">
                  {t("Todo el audio del computador", "All the computer's audio")}
                  <span className="block text-xs text-muted-foreground mt-0.5 leading-5">
                    {t("Graba el sonido de cualquier programa, no solo del navegador: sirve con Zoom, Teams o Meet instalados en el computador.",
                       "Records sound from any program, not just the browser: it works with Zoom, Teams or Meet installed on the computer.")}
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={captureMic}
                  onChange={(e) => { setCaptureMic(e.target.checked); if (!e.target.checked) stopProbe(); }}
                  className="mt-1 h-4 w-4 accent-[color:hsl(var(--primary))]"
                  data-testid="live-capture-mic-toggle"
                />
                <span className="text-sm">
                  {t("Mi voz (micrófono)", "My voice (microphone)")}
                  <span className="block text-xs text-muted-foreground mt-0.5 leading-5">
                    {t("Tu propia voz no sale por los parlantes, así que sin esto tu intervención no queda en la grabación.",
                       "Your own voice never comes out of the speakers, so without this your own words are not recorded.")}
                  </span>
                </span>
              </label>

              {captureMic && (
                <div className="mt-3 pl-7">
                  <label className="block text-xs text-muted-foreground mb-1.5" htmlFor="live-device">
                    {t("Micrófono", "Microphone")}
                  </label>
                  <Select value={deviceId} onValueChange={(v) => { setDeviceId(v); stopProbe(); }}>
                    <SelectTrigger id="live-device" data-testid="live-device-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">{t("Predeterminado del sistema", "System default")}</SelectItem>
                      {devices
                        .filter((d) => d.deviceId && d.deviceId !== "default")
                        .map((d, i) => (
                          <SelectItem key={d.deviceId} value={d.deviceId}>
                            {d.label || `${t("Entrada", "Input")} ${i + 1}`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Button type="button" variant="outline" size="sm" onClick={probing ? stopProbe : probeDevice}
                            data-testid="live-probe-button">
                      {probing ? t("Detener prueba", "Stop test") : t("Probar micrófono", "Test microphone")}
                    </Button>
                    {probing && (
                      <div className="flex items-center gap-2" data-testid="live-probe-meter">
                        <div className="h-2 w-32 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full transition-[width] duration-75"
                            style={{
                              width: `${Math.round(probeLevel * 100)}%`,
                              background: probeLevel > 0.04 ? "hsl(var(--primary))" : "#71717a",
                            }}
                          />
                        </div>
                        <span className={`text-xs ${probeLevel > 0.04 ? "text-emerald-400" : "text-yellow-400"}`}>
                          {probeLevel > 0.04 ? t("recibiendo audio", "receiving audio") : t("sin señal", "no signal")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {captureMeeting && (
              <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-yellow-500/60 bg-yellow-500/15 p-4">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-yellow-400" />
                <div>
                  <p className="text-sm font-bold text-yellow-200 leading-6">
                    {audioSupport && !audioSupport.systemAudio
                      ? t("Este computador solo puede grabar el audio de una pestaña del navegador.",
                          "This computer can only record one browser tab's audio.")
                      : t("Comparte «Toda la pantalla», no una pestaña, y marca la casilla del audio.",
                          "Share \u201cEntire screen\u201d \u2014 not a tab \u2014 and tick the audio box.")}
                  </p>
                  <p className="mt-1 text-xs text-yellow-200/80 leading-5">
                    {audioSupport && !audioSupport.systemAudio
                      ? t("El audio tiene que venir de una pestaña del navegador. Te mostramos cómo antes de empezar.",
                          "The audio must come from a browser tab. We show you how before you start.")
                      : t("Te mostramos cómo hacerlo antes de empezar. Es la única forma de capturar el sonido de Zoom o Teams instalados en el computador.",
                          "We show you how before you start. It is the only way to capture sound from Zoom or Teams installed on the computer.")}
                  </p>
                </div>
              </div>
            )}

            <Button className="mt-4 btn-glow" onClick={() => (captureMeeting ? setShowGuide(true) : arm())}
                    disabled={!transcriptionLanguage || (!captureMeeting && !captureMic) || (captureMeeting && !browserSupported)}
                    data-testid="live-arm-button">
              <Mic className="w-4 h-4 mr-2" />
              {captureMeeting ? t("Elegir qué compartir", "Choose what to share") : t("Preparar grabación", "Prepare recording")}
            </Button>

            <ShareAudioGuide
              open={showGuide}
              onOpenChange={setShowGuide}
              support={audioSupport}
              onContinue={() => { setShowGuide(false); arm(); }}
            />
          </>
        )}
      </div>

      {(phase === "armed" || phase === "recording" || phase === "paused") && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-col items-center gap-3">
            <VoxaraScribe levelRef={levelRef} active={isLive} />

            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="live-status">
              <span className={`w-2 h-2 rounded-full ${isLive && !silent ? "bg-emerald-400" : "bg-yellow-400"}`} />
              <span>{statusText()}</span>
            </div>

            <div className="font-mono text-3xl tabular-nums" data-testid="live-timer">{fmtClock(elapsed)}</div>

            <div className="flex flex-wrap gap-2 justify-center">
              {phase === "armed" && (
                <Button className="btn-glow" onClick={beginRecording} data-testid="live-start-button">
                  <Mic className="w-4 h-4 mr-2" />{t("Iniciar grabación", "Start recording")}
                </Button>
              )}
              {(isLive || phase === "paused") && (
                <>
                  <Button variant="outline" onClick={togglePause} data-testid="live-pause-button">
                    {isLive ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {isLive ? t("Pausar", "Pause") : t("Reanudar", "Resume")}
                  </Button>
                  <Button className="btn-glow" onClick={stopRecording} data-testid="live-stop-button">
                    <Square className="w-4 h-4 mr-2" />{t("Detener y procesar", "Stop and process")}
                  </Button>
                </>
              )}
              {phase === "armed" && (
                <Button variant="outline" onClick={reset}>{t("Cancelar", "Cancel")}</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {(phase === "uploading" || phase === "done") && result && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="live-result">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="mx-auto w-44 shrink-0 sm:mx-0 sm:w-48">
              <VoxaraWaiting />
            </div>

            <div className="min-w-0 flex-1">
              <h4 className="font-semibold">
                {result.expired
                  ? t("La copia ya se borró", "The copy has been deleted")
                  : t("Guarda tu copia del audio ahora", "Save your copy of the audio now")}
              </h4>

              {result.expired ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("Pasaron los 30 minutos y borramos el audio de este navegador. Tu transcripción está a salvo en el dashboard: ahí quedan el texto, los hablantes, los momentos clave y el resumen.",
                     "The 30 minutes elapsed and we deleted the audio from this browser. Your transcript is safe in the dashboard: the text, speakers, key moments and summary are all there.")}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("Conservamos esta copia en tu navegador durante 30 minutos y después la borramos automáticamente, por seguridad y privacidad. En nuestros servidores el audio se elimina apenas termina el análisis.",
                       "We keep this copy in your browser for 30 minutes and then delete it automatically, for security and privacy. On our servers the audio is deleted as soon as the analysis finishes.")}
                  </p>
                  {copySecondsLeft !== null && (
                    <p className="mt-2 text-sm font-semibold text-primary" data-testid="live-copy-countdown">
                      {t(`Se borra en ${fmtCountdown(copySecondsLeft)}`, `Deletes in ${fmtCountdown(copySecondsLeft)}`)}
                    </p>
                  )}
                </>
              )}

              <p className="font-mono text-xs text-muted-foreground mt-2">
                {result.name} · {fmtSize(result.size)}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {!result.expired && (
                  <Button asChild className="btn-glow">
                    <a href={result.url} download={result.name} data-testid="live-download-button">
                      <Download className="w-4 h-4 mr-2" />{t("Descargar audio", "Download audio")}
                    </a>
                  </Button>
                )}
                {pendingUpload && phase !== "uploading" && (
                  <Button
                    variant="outline"
                    onClick={() => sendToPipeline(pendingUpload.blob, pendingUpload.name,
                                                  pendingUpload.language, pendingUpload.recordingType)}
                    data-testid="live-resend-button"
                  >
                    {t("Enviar a transcribir", "Send for transcription")}
                  </Button>
                )}
                {phase === "done" && (
                  <Button variant="outline" onClick={reset} data-testid="live-new-button">
                    {t("Grabar otra", "Record another")}
                  </Button>
                )}
              </div>

              <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
                {phase === "uploading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin text-primary" />
                    <span>{t(`Subiendo para procesar… ${uploadPct}%`, `Uploading for processing… ${uploadPct}%`)}</span>
                  </>
                ) : pendingUpload ? (
                  <>
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
                    <span>{t("Esta grabación todavía no llegó a transcribirse. Tu copia está aquí; envíala cuando quieras.",
                             "This recording has not reached transcription yet. Your copy is here; send it whenever you want.")}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                    <span>{t("Enviada al pipeline. Aparecerá en el dashboard con transcripción, hablantes, momentos clave y resumen.",
                             "Sent to the pipeline. It will appear in the dashboard with transcript, speakers, key moments and summary.")}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
